import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";

const FocusContainer = styled.div`
  position: relative;
  display: flex;
  gap: 1em;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const FocusWord = styled.span`
  position: relative;
  font-size: clamp(2.5rem, 8vw, 3rem);
  font-weight: 900;
  cursor: pointer;
  transition: filter 0.3s ease, color 0.3s ease;
  filter: ${props => props.isActive ? 'blur(0px)' : `blur(${props.blurAmount}px)`};
  color: ${props => props.isActive ? '#00ff00' : '#ffffff'};
  text-shadow: ${props => props.isActive ? '0 0 20px rgba(0, 255, 0, 0.5)' : '0 0 10px rgba(255, 255, 255, 0.3)'};
`;

const FocusFrame = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  box-sizing: content-box;
  border: none;
`;

const Corner = styled.span`
  position: absolute;
  width: 1rem;
  height: 1rem;
  border: 3px solid ${props => props.borderColor || '#00ff00'};
  filter: drop-shadow(0px 0px 4px ${props => props.borderColor || '#00ff00'});
  border-radius: 3px;
  transition: none;

  &.top-left {
    top: -10px;
    left: -10px;
    border-right: none;
    border-bottom: none;
  }

  &.top-right {
    top: -10px;
    right: -10px;
    border-left: none;
    border-bottom: none;
  }

  &.bottom-left {
    bottom: -10px;
    left: -10px;
    border-right: none;
    border-top: none;
  }

  &.bottom-right {
    bottom: -10px;
    right: -10px;
    border-left: none;
    border-top: none;
  }
`;

const TrueFocus = ({
  sentence = "True Focus",
  manualMode = false,
  blurAmount = 5,
  borderColor = "#00ff00",
  glowColor = "rgba(0, 255, 0, 0.6)",
  animationDuration = 0.5,
  pauseBetweenAnimations = 1,
}) => {
  const words = sentence.split(" ");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastActiveIndex, setLastActiveIndex] = useState(null);
  const containerRef = useRef(null);
  const wordRefs = useRef([]);
  const [focusRect, setFocusRect] = useState({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!manualMode) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % words.length);
      }, (animationDuration + pauseBetweenAnimations) * 1000);

      return () => clearInterval(interval);
    }
  }, [manualMode, animationDuration, pauseBetweenAnimations, words.length]);

  useEffect(() => {
    if (currentIndex === null || currentIndex === -1) return;

    if (!wordRefs.current[currentIndex] || !containerRef.current) return;

    const parentRect = containerRef.current.getBoundingClientRect();
    const activeRect = wordRefs.current[currentIndex].getBoundingClientRect();

    setFocusRect({
      x: activeRect.left - parentRect.left,
      y: activeRect.top - parentRect.top,
      width: activeRect.width,
      height: activeRect.height,
    });
  }, [currentIndex, words.length]);

  const handleMouseEnter = (index) => {
    if (manualMode) {
      setLastActiveIndex(index);
      setCurrentIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (manualMode) {
      setCurrentIndex(lastActiveIndex);
    }
  };

  return (
    <FocusContainer ref={containerRef}>
      {words.map((word, index) => {
        const isActive = index === currentIndex;
        return (
          <FocusWord
            key={index}
            ref={(el) => (wordRefs.current[index] = el)}
            isActive={isActive}
            blurAmount={blurAmount}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            {word}
          </FocusWord>
        );
      })}

      <FocusFrame
        animate={{
          x: focusRect.x,
          y: focusRect.y,
          width: focusRect.width,
          height: focusRect.height,
          opacity: currentIndex >= 0 ? 1 : 0,
        }}
        transition={{
          duration: animationDuration,
        }}
      >
        <Corner className="top-left" borderColor={borderColor} />
        <Corner className="top-right" borderColor={borderColor} />
        <Corner className="bottom-left" borderColor={borderColor} />
        <Corner className="bottom-right" borderColor={borderColor} />
      </FocusFrame>
    </FocusContainer>
  );
};

export default TrueFocus;
